import { productUnits } from '@eneco/enumerations/productUnits';

import { type CostsType, type ProductFormatter } from './types';
import type { Details } from '../../../../shared/components/PriceDetails/PriceDetails';
import type { ContentProps, FooterProps } from '../../../../shared/components/PriceDetails/PriceDetails.types';
import type { CostDetails } from '../../../../shared/types';
import type { PriceDetailsOverviewTextualData } from '../price-details-overview.types';

interface TaxBracketDetails {
  active?: boolean;
  usageMin?: number;
  usageMax?: number;
  price?: Partial<Record<CostsType, number>>;
}

export interface ProductSectionsBuilderContext {
  costDetails?: CostDetails;
  costsType: CostsType;
  textualData?: PriceDetailsOverviewTextualData;
  hasDoubleMeter?: boolean;
  hasTimeOfUse: boolean;
  shouldShowDynamicPricingLabels: boolean;
  shouldShowTaxBracketsPriceComponents: boolean;
  electricityNormal: number;
  electricityLow: number;
  gas: number;
  estimatedElectricityLow?: number | null;
  estimatedElectricityHigh?: number | null;
  electricityUsageTotal: number;
  gasUsageTotal: number;
  isDynamicProduct: boolean;
  formatter: ProductFormatter;
}

interface ProductContentBlock {
  summary: ContentProps['summary'];
  details?: Details[];
}

interface ProductFooterBlock {
  summary: FooterProps['summary'];
  details?: Details[];
}

export interface ElectricitySectionViewModel {
  title: string;
  variableCosts: ProductContentBlock;
  governmentLevies?: ProductContentBlock;
  fixedCosts: ProductContentBlock;
  redelivery?: ProductContentBlock;
  footer: ProductFooterBlock;
}

export interface GasSectionViewModel {
  title: string;
  variableCosts: ProductContentBlock;
  governmentLevies?: ProductContentBlock;
  fixedCosts: ProductContentBlock;
  footer: ProductFooterBlock;
}

export interface WarmthSectionViewModel {
  title: string;
  variableCosts: ProductContentBlock;
  fixedCosts: ProductContentBlock;
  footer: ProductFooterBlock;
}

export interface TapWaterSectionViewModel {
  title: string;
  variableCosts: ProductContentBlock;
  footer: ProductFooterBlock;
}

export interface WarmthExchangeSetSectionViewModel {
  title: string;
  contents: ProductContentBlock[];
  footer: ProductFooterBlock;
}

export interface GeothermalHeatingSectionViewModel {
  title: string;
  contents: ProductContentBlock[];
  footer: ProductFooterBlock;
}

const getDeliveryCostsComponentPrice = (
  tariff:
    | {
        components?: Array<{ type: string } & Partial<Record<CostsType, number>>>;
      }
    | undefined,
  costsType: CostsType,
): number => tariff?.components?.find(component => component.type === 'deliverycosts')?.[costsType] ?? 0;

const buildTaxBracketDetails = ({
  taxDetails,
  costsType,
  productUnit,
  taxBracketLabel,
  usageTotal,
  usageLabel,
  yearlyEnergyTax,
  formatter: { currency, number },
  formatUsage,
}: {
  taxDetails?: Record<string, TaxBracketDetails>;
  costsType: CostsType;
  productUnit: string;
  taxBracketLabel?: string;
  usageTotal: number;
  usageLabel?: string;
  yearlyEnergyTax?: number;
  formatter: ProductFormatter;
  formatUsage: (value: number) => string;
}): Details[] => [
  ...Object.keys(taxDetails ?? {}).reduce<Details[]>((acc, key) => {
    const bracket = taxDetails?.[key];

    if (bracket?.active) {
      acc.push({
        text: `${currency.euroHighPrecision(bracket.price?.[costsType] ?? 0)}/${productUnit}`,
        value: `${number.noFractionDigits(bracket.usageMin ?? 0)} - ${number.noFractionDigits(
          bracket.usageMax ?? 0,
        )} ${taxBracketLabel}
        `,
      });
    }

    return acc;
  }, []),
  {
    text: `${usageLabel}: ${formatUsage(usageTotal)}`,
    value: currency.euroHighPrecision(yearlyEnergyTax ?? 0),
    highlightDivider: true,
    highlightValue: true,
  },
];

const buildPurchaseFeeDetails = ({
  marketPrice,
  marketTotal,
  purchaseFeePrice,
  purchaseFeeTotal,
  usage,
  formatUsage,
  formatter: { currency },
}: {
  marketPrice?: number;
  marketTotal?: number;
  purchaseFeePrice?: number;
  purchaseFeeTotal?: number;
  usage: number;
  formatUsage: (value: number) => string;
  formatter: ProductFormatter;
}): Details[] => [
  {
    text: 'Geschatte marktprijs',
    highlightText: true,
  },
  {
    text: `${currency.euroFiveDecimals(marketPrice ?? 0)} × ${formatUsage(usage)} =`,
    value: marketTotal,
    highlightDivider: true,
    highlightValue: true,
  },
  {
    text: 'Inkoopvergoeding',
    highlightText: true,
  },
  {
    text: `${currency.euroFiveDecimals(purchaseFeePrice ?? 0)} × ${formatUsage(usage)} =`,
    value: purchaseFeeTotal,
    highlightDivider: true,
    highlightValue: true,
  },
];

const buildElectricityDoubleMeterVariableDetails = ({
  context,
  electricityCostDetails,
}: {
  context: ProductSectionsBuilderContext;
  electricityCostDetails: NonNullable<PriceDetailsOverviewTextualData>['electricityCostDetails'];
}): Details[] => {
  const {
    costDetails,
    costsType,
    hasTimeOfUse,
    shouldShowDynamicPricingLabels,
    estimatedElectricityHigh,
    estimatedElectricityLow,
    electricityNormal,
    electricityLow,
    isDynamicProduct,
    formatter: { currency, unit },
  } = context;

  if (isDynamicProduct && costDetails?.electricityCostDetails?.purchaseFee?.price?.vatIncluded) {
    return buildPurchaseFeeDetails({
      marketPrice: costDetails?.electricityCostDetails?.tariffWithoutTax?.price?.[costsType],
      marketTotal: costDetails?.electricityTotal?.yearlyCostsVariableWithoutTax?.[costsType],
      purchaseFeePrice: costDetails?.electricityCostDetails?.purchaseFee?.price?.[costsType],
      purchaseFeeTotal: costDetails?.electricityTotal?.purchaseFee?.[costsType],
      usage: costDetails?.electricityTotal?.purchaseFee?.usage ?? 0,
      formatUsage: unit.electricity,
      formatter: context.formatter,
    });
  }

  const deliveryCostsLabel =
    electricityCostDetails?.[
      shouldShowDynamicPricingLabels ? 'tariffDynamicPricingProductDeliveryCostsLabel' : 'tariffDeliveryCostsLabel'
    ]?.value;

  return [
    {
      text: hasTimeOfUse
        ? electricityCostDetails?.timeOfUseTariffLabel?.value
        : electricityCostDetails?.tariffLabel?.value,
      highlightText: true,
    },
    {
      text: `${currency.euroHighPrecision(
        getDeliveryCostsComponentPrice(costDetails?.electricityCostDetails?.tariffWithoutTax, costsType),
      )}/${productUnits.electricity}`,
      value: deliveryCostsLabel,
    },
    {
      text: `${currency.euroFiveDecimals(
        costDetails?.electricityCostDetails?.tariffWithoutTax?.price?.[costsType],
      )} × ${unit.electricity(estimatedElectricityHigh ?? electricityNormal ?? 0)} = `,
      value: costDetails?.electricityTotal?.yearlyCostsVariableHighWithoutTax?.[costsType],
      highlightDivider: true,
      highlightValue: true,
    },
    {
      text: hasTimeOfUse
        ? electricityCostDetails?.timeOfUseLowTariffLabel?.value
        : electricityCostDetails?.lowTarifflLabel?.value,
      highlightText: true,
    },
    {
      text: `${currency.euroFiveDecimals(
        getDeliveryCostsComponentPrice(costDetails?.electricityCostDetails?.lowTariffWithoutTax, costsType),
      )}/${productUnits.electricity}`,
      value: deliveryCostsLabel,
    },
    {
      text: `${currency.euroFiveDecimals(
        costDetails?.electricityCostDetails?.lowTariffWithoutTax?.price?.[costsType],
      )} × ${unit.electricity(estimatedElectricityLow ?? electricityLow ?? 0)} =`,
      value: costDetails?.electricityTotal?.yearlyCostsVariableLowWithoutTax?.[costsType],
      highlightDivider: true,
      highlightValue: true,
    },
  ];
};

const buildElectricitySingleMeterVariableDetails = (context: ProductSectionsBuilderContext): Details[] => {
  const {
    costDetails,
    costsType,
    shouldShowDynamicPricingLabels,
    estimatedElectricityHigh,
    electricityNormal,
    textualData,
    formatter: { currency, unit },
  } = context;

  return [
    {
      text: `${currency.euroFiveDecimals(
        getDeliveryCostsComponentPrice(costDetails?.electricityCostDetails?.tariffWithoutTax, costsType),
      )}/${productUnits.electricity}`,
      value:
        textualData?.electricityCostDetails?.[
          shouldShowDynamicPricingLabels ? 'tariffDynamicPricingProductDeliveryCostsLabel' : 'tariffDeliveryCostsLabel'
        ]?.value,
    },
    {
      text: `${currency.euroFiveDecimals(
        costDetails?.electricityCostDetails?.tariffWithoutTax?.price?.[costsType],
      )} × ${unit.electricity(estimatedElectricityHigh ?? electricityNormal ?? 0)} = `,
      value: costDetails?.electricityTotal?.yearlyCostsVariableWithoutTax?.[costsType],
      highlightDivider: true,
      highlightValue: true,
    },
  ];
};

export const buildElectricityVariableCostDetails = (context: ProductSectionsBuilderContext): Details[] =>
  context.hasDoubleMeter
    ? buildElectricityDoubleMeterVariableDetails({
        context,
        electricityCostDetails: context.textualData?.electricityCostDetails,
      })
    : buildElectricitySingleMeterVariableDetails(context);

export interface BuildElectricitySectionParams {
  context: ProductSectionsBuilderContext;
  redelivery?: ProductContentBlock;
}

export const buildElectricitySection = ({
  context,
  redelivery,
}: BuildElectricitySectionParams): ElectricitySectionViewModel => {
  const { costDetails, costsType, textualData, shouldShowTaxBracketsPriceComponents, isDynamicProduct, formatter } =
    context;
  const { currency } = formatter;
  const electricityCostDetails = textualData?.electricityCostDetails;
  const sharedCostDetails = textualData?.costDetails;

  return {
    title: textualData?.productTypeLabels?.electricityProductLabel?.value ?? '',
    variableCosts: {
      summary: {
        text: `${sharedCostDetails?.variableCostsLabel?.value}`,
        value: isDynamicProduct
          ? costDetails?.electricityTotal?.yearlyCostsVariable?.[costsType]
          : costDetails?.electricityTotal?.yearlyCostsVariableWithoutTax?.[costsType],
      },
      details: buildElectricityVariableCostDetails(context),
    },
    governmentLevies: shouldShowTaxBracketsPriceComponents
      ? {
          summary: {
            text: electricityCostDetails?.governmentLeviesLabel?.value,
            value: currency.euroHighPrecision(costDetails?.electricityTotal?.yearlyEnergyTax?.[costsType]),
          },
          details: buildTaxBracketDetails({
            taxDetails: costDetails?.electricityEnergyTaxDetails,
            costsType,
            productUnit: productUnits.electricity,
            taxBracketLabel: electricityCostDetails?.taxBracket1Label?.value,
            usageTotal: context.electricityUsageTotal,
            usageLabel: sharedCostDetails?.usageLabel?.value,
            yearlyEnergyTax: costDetails?.electricityTotal?.yearlyEnergyTax?.[costsType],
            formatter,
            formatUsage: formatter.unit.electricity,
          }),
        }
      : undefined,
    fixedCosts: {
      summary: {
        text: sharedCostDetails?.fixedCostsLabel?.value,
        value: costDetails?.electricityTotal?.yearlyCostsFixed?.[costsType],
      },
      details: [
        {
          text: electricityCostDetails?.standingChargeLabel?.value,
          value: costDetails?.electricityTotal?.standingCharge?.[costsType],
        },
        {
          text:
            electricityCostDetails?.deliveryCostsLabel?.value?.replace(
              '{value}',
              costDetails?.electricityCostDetails?.deliverycosts?.gridOperatorName ?? '',
            ) ?? '',
          value: costDetails?.electricityCostDetails?.deliverycosts?.price?.[costsType],
        },
        {
          text: electricityCostDetails?.taxDiscountLabel?.value,
          value: costDetails?.electricityCostDetails?.taxdiscount?.price?.[costsType],
        },
      ],
    },
    redelivery,
    footer: {
      summary: {
        label: sharedCostDetails?.monthlyTermLabel?.value,
        value: costDetails?.electricityTotal?.monthlyCosts?.[costsType],
      },
      details: [
        {
          text: sharedCostDetails?.yearlyTermLabel?.value,
          value: costDetails?.electricityTotal?.yearlyCosts?.[costsType],
          highlightDivider: true,
        },
      ],
    },
  };
};

export const buildGasSection = (context: ProductSectionsBuilderContext): GasSectionViewModel => {
  const { costDetails, costsType, textualData, shouldShowTaxBracketsPriceComponents, formatter } = context;
  const { currency, unit } = formatter;
  const gasCostDetails = textualData?.gasCostDetails;
  const sharedCostDetails = textualData?.costDetails;
  const hasGasPurchaseFee =
    Number.isFinite(costDetails?.gasTotal?.purchaseFee?.[costsType]) &&
    Number(costDetails?.gasTotal?.purchaseFee?.[costsType]) > 0;

  const variableDetails: Details[] = hasGasPurchaseFee
    ? buildPurchaseFeeDetails({
        marketPrice: costDetails?.gasCostDetails?.tariffWithoutTax?.price?.[costsType],
        marketTotal: costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType],
        purchaseFeePrice: costDetails?.gasCostDetails?.purchaseFee?.price?.[costsType],
        purchaseFeeTotal: costDetails?.gasTotal?.purchaseFee?.[costsType],
        usage: costDetails?.gasTotal?.purchaseFee?.usage ?? 0,
        formatUsage: unit.gas,
        formatter,
      })
    : [
        {
          text: `${currency.euroFiveDecimals(
            getDeliveryCostsComponentPrice(costDetails?.gasCostDetails?.tariffWithoutTax, costsType),
          )}/${productUnits.gas}`,
          value: gasCostDetails?.tariffLabel?.value,
        },
        {
          text: `${currency.euroFiveDecimals(
            costDetails?.gasCostDetails?.tariffWithoutTax?.price?.[costsType],
          )} × ${unit.gas(context.gas ?? 0)} =`,
          value: costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType],
          highlightDivider: true,
          highlightValue: true,
        },
      ];

  return {
    title: textualData?.productTypeLabels?.gasProductLabel?.value ?? '',
    variableCosts: {
      summary: {
        text: sharedCostDetails?.variableCostsLabel?.value,
        value: hasGasPurchaseFee
          ? costDetails?.gasTotal?.yearlyCostsVariable?.[costsType]
          : costDetails?.gasTotal?.yearlyCostsVariableWithoutTax?.[costsType],
      },
      details: variableDetails,
    },
    governmentLevies: shouldShowTaxBracketsPriceComponents
      ? {
          summary: {
            text: gasCostDetails?.governmentLeviesLabel?.value,
            value: currency.euroHighPrecision(costDetails?.gasTotal?.yearlyEnergyTax?.[costsType]),
          },
          details: buildTaxBracketDetails({
            taxDetails: costDetails?.gasEnergyTaxDetails,
            costsType,
            productUnit: productUnits.gas,
            taxBracketLabel: gasCostDetails?.taxBracket1Label?.value,
            usageTotal: context.gasUsageTotal,
            usageLabel: sharedCostDetails?.usageLabel?.value,
            yearlyEnergyTax: costDetails?.gasTotal?.yearlyEnergyTax?.[costsType],
            formatter,
            formatUsage: unit.gas,
          }),
        }
      : undefined,
    fixedCosts: {
      summary: {
        text: sharedCostDetails?.fixedCostsLabel?.value,
        value: costDetails?.gasTotal?.yearlyCostsFixed?.[costsType],
      },
      details: [
        {
          text: gasCostDetails?.standingChargeLabel?.value,
          value: costDetails?.gasTotal?.standingCharge?.[costsType] ?? 0,
        },
        {
          text: `${gasCostDetails?.deliveryCostsLabel?.value.replace('{value}', costDetails?.gasCostDetails?.deliverycosts?.gridOperatorName ?? '') ?? ''}`,
          value: costDetails?.gasCostDetails?.deliverycosts?.price?.[costsType],
        },
      ],
    },
    footer: {
      summary: {
        label: sharedCostDetails?.monthlyTermLabel?.value,
        value: costDetails?.gasTotal?.monthlyCosts?.[costsType],
      },
      details: [
        {
          text: sharedCostDetails?.yearlyTermLabel?.value,
          value: costDetails?.gasTotal?.yearlyCosts?.[costsType],
          highlightDivider: true,
        },
      ],
    },
  };
};

export const buildWarmthSection = (context: ProductSectionsBuilderContext): WarmthSectionViewModel => {
  const { costDetails, costsType, textualData, formatter } = context;
  const warmthCostDetails = textualData?.warmthCostDetails;
  const sharedCostDetails = textualData?.costDetails;
  const standingChargeDiscountPrice =
    costDetails?.warmthCostDetails?.standingchargediscount?.price?.[costsType] ||
    costDetails?.warmthEkvCostDetails?.standingchargediscount?.price?.[costsType];

  return {
    title: textualData?.productTypeLabels?.warmthProductLabel?.value ?? '',
    variableCosts: {
      summary: {
        text: sharedCostDetails?.variableCostsLabel?.value,
        value:
          costDetails?.warmthTotal?.yearlyCostsVariable?.[costsType] ||
          costDetails?.warmthEkvTotal?.yearlyCostsVariable?.[costsType],
      },
      details: [
        {
          text: `${formatter.currency.euroFiveDecimals(
            costDetails?.warmthCostDetails?.tariff?.price?.[costsType] ||
              costDetails?.warmthEkvCostDetails?.tariff?.price?.[costsType],
          )}/${productUnits.warmth}`,
          value: warmthCostDetails?.tariffLabel?.value,
        },
      ],
    },
    fixedCosts: {
      summary: {
        text: sharedCostDetails?.fixedCostsLabel?.value,
        value:
          costDetails?.warmthTotal?.yearlyCostsFixed?.[costsType] ||
          costDetails?.warmthEkvTotal?.yearlyCostsFixed?.[costsType],
      },
      details: [
        {
          text: `${warmthCostDetails?.deliveryCostsLabel?.value?.replace(
            '{value}',
            costDetails?.warmthCostDetails?.deliverycosts?.gridOperatorName ??
              costDetails?.warmthEkvCostDetails?.deliverycosts?.gridOperatorName ??
              '',
          )}`,
          value:
            costDetails?.warmthCostDetails?.deliverycosts?.price?.[costsType] ||
            costDetails?.warmthEkvCostDetails?.deliverycosts?.price?.[costsType],
        },
        ...(standingChargeDiscountPrice && warmthCostDetails?.standingChargeDiscountLabel?.value
          ? [
              {
                text: warmthCostDetails.standingChargeDiscountLabel.value,
                value: standingChargeDiscountPrice,
              },
            ]
          : []),
        {
          text: warmthCostDetails?.standingChargeLabel?.value,
          value:
            costDetails?.warmthCostDetails?.standingcharge?.price?.[costsType] ||
            costDetails?.warmthEkvCostDetails?.standingcharge?.price?.[costsType],
        },
      ],
    },
    footer: {
      summary: {
        label: sharedCostDetails?.monthlyTermLabel?.value,
        value:
          costDetails?.warmthTotal?.monthlyCosts?.[costsType] ?? costDetails?.warmthEkvTotal?.monthlyCosts?.[costsType],
      },
      details: [
        {
          text: sharedCostDetails?.yearlyTermLabel?.value,
          value:
            costDetails?.warmthTotal?.yearlyCosts?.[costsType] ?? costDetails?.warmthEkvTotal?.yearlyCosts?.[costsType],
          highlightDivider: true,
        },
      ],
    },
  };
};

export const buildTapWaterSection = (context: ProductSectionsBuilderContext): TapWaterSectionViewModel => {
  const { costDetails, costsType, textualData, formatter } = context;
  const { currency } = formatter;
  const warmthCostDetails = textualData?.warmthCostDetails;
  const sharedCostDetails = textualData?.costDetails;
  const tapwaterCostDetails = costDetails?.tapwaterCostDetails;
  const denotationType = tapwaterCostDetails?.tariff?.denotationType ?? '';

  const details: Details[] = [];

  if (tapwaterCostDetails?.tariffcoldwater?.price?.[costsType]) {
    details.push({
      text: `${currency.euroFiveDecimals(tapwaterCostDetails.tariffcoldwater.price[costsType])}/${denotationType}`,
      value: warmthCostDetails?.tariffColdWaterLabel?.value,
    });
  }

  if (tapwaterCostDetails?.taxcoldwater?.price?.[costsType]) {
    details.push({
      text: `${currency.euroFiveDecimals(tapwaterCostDetails.taxcoldwater.price[costsType])}/${denotationType}`,
      value: warmthCostDetails?.taxColdWaterLabel?.value,
    });
  }

  if (tapwaterCostDetails?.tariff?.price?.[costsType]) {
    details.push({
      text: `${currency.euroFiveDecimals(tapwaterCostDetails.tariff.price[costsType])}/${denotationType}`,
      value: warmthCostDetails?.tariffWarmWaterLabel?.value,
    });
  }

  return {
    title: textualData?.productTypeLabels?.waterProductLabel?.value ?? '',
    variableCosts: {
      summary: {
        text: sharedCostDetails?.variableCostsLabel?.value,
        value: costDetails?.tapwaterTotal?.yearlyCostsVariable?.[costsType],
      },
      details,
    },
    footer: {
      summary: {
        label: sharedCostDetails?.monthlyTermLabel?.value,
        value: costDetails?.tapwaterTotal?.monthlyCosts?.[costsType] ?? 0,
      },
      details: [
        {
          text: sharedCostDetails?.yearlyTermLabel?.value,
          value: costDetails?.tapwaterTotal?.yearlyCosts?.[costsType],
          highlightDivider: true,
        },
      ],
    },
  };
};

export const buildWarmthExchangeSetSection = (
  context: ProductSectionsBuilderContext,
): WarmthExchangeSetSectionViewModel => {
  const { costDetails, costsType, textualData } = context;
  const warmthCostDetails = textualData?.warmthCostDetails;
  const sharedCostDetails = textualData?.costDetails;
  const contents: ProductContentBlock[] = [
    {
      summary: {
        text: warmthCostDetails?.rentWarmthSet?.value,
        value: costDetails?.warmtewisselaarCostDetails?.rentwarmthset?.price?.[costsType],
      },
    },
  ];

  if (costDetails?.warmtewisselaarCostDetails?.standingchargediscount?.price?.[costsType]) {
    contents.push({
      summary: {
        text: warmthCostDetails?.discountLabel?.value,
        value: costDetails.warmtewisselaarCostDetails.standingchargediscount.price[costsType],
      },
    });
  }

  return {
    title: textualData?.productTypeLabels?.warmthExchangeSetLabel?.value ?? '',
    contents,
    footer: {
      summary: {
        label: sharedCostDetails?.totalCostsLabel?.value,
        value: costDetails?.warmtewisselaarTotal?.yearlyCosts?.[costsType],
      },
    },
  };
};

export const buildGeothermalHeatingSection = (
  context: ProductSectionsBuilderContext,
): GeothermalHeatingSectionViewModel => {
  const { costDetails, costsType, textualData, formatter } = context;
  const warmthCostDetails = textualData?.warmthCostDetails;
  const sharedCostDetails = textualData?.costDetails;
  const contents: ProductContentBlock[] = [
    {
      summary: {
        text: warmthCostDetails?.standingChargeGeothermalHeatingLabel?.value,
        value: costDetails?.bronwarmteCostDetails?.standingcharge?.price?.[costsType],
      },
    },
  ];

  if (
    costDetails?.bronwarmteCostDetails?.standingChargeSurcharge?.capacity &&
    costDetails?.bronwarmteCostDetails?.standingChargeSurcharge?.price?.[costsType] &&
    costDetails?.bronwarmteTotal?.standingChargeSurcharge?.[costsType]
  ) {
    contents.push({
      summary: {
        text: warmthCostDetails?.variableCostsGeothermalHeatingLabel?.value,
        value: costDetails.bronwarmteTotal.standingChargeSurcharge[costsType],
      },
      details: [
        {
          text: warmthCostDetails?.variableCostsGeothermalHeatingCapacityLabel?.value,
          value: `${formatter.unit.thermalPower(costDetails.bronwarmteCostDetails.standingChargeSurcharge.capacity)}`,
        },
        {
          text: warmthCostDetails?.variableCostsGeothermalHeatingPricePerCapacityLabel?.value,
          value: costDetails.bronwarmteCostDetails.standingChargeSurcharge.price[costsType],
        },
      ],
    });
  }

  contents.push({
    summary: {
      text: warmthCostDetails?.geothermalHeatingPompCostsLabel?.value,
      value: costDetails?.bronwarmteTotal?.rentalDevice?.[costsType],
    },
  });

  if (costDetails?.bronwarmteCostDetails?.deliverycosts?.price?.[costsType]) {
    contents.push({
      summary: {
        text: `${warmthCostDetails?.deliveryCostsLabel?.value}`,
        value: costDetails.bronwarmteCostDetails.deliverycosts.price[costsType],
      },
    });
  }

  return {
    title: textualData?.productTypeLabels?.geothermalHeatingProductLabel?.value ?? '',
    contents,
    footer: {
      summary: {
        label: sharedCostDetails?.monthlyTermLabel?.value,
        value: costDetails?.bronwarmteTotal?.monthlyCosts?.[costsType],
      },
      details: [
        {
          text: sharedCostDetails?.yearlyTermLabel?.value,
          value: costDetails?.bronwarmteTotal?.yearlyCosts?.[costsType],
          highlightDivider: true,
        },
      ],
    },
  };
};

export const hasWarmthExchangeSetSection = (context: ProductSectionsBuilderContext): boolean =>
  Boolean(context.costDetails?.warmtewisselaarCostDetails?.rentwarmthset?.price);
